import { INVENTORY_ORIGINS, type InventoryOrigin } from "../types";

/**
 * Almikia Epic EC2d (TASK-137): pure rules behind the Inventario Almikia board filters.
 * The list keeps its filter state in the query shape the `inventory_items` view understands
 * (`field@operator`, see `inventoryItemsProvider.ts`), so these helpers only translate between
 * what the controls show and that shape. Every function returns a new object.
 *
 * Filter semantics (also recorded in docs/almikia/QUESTIONS.md): counts (levels, bedrooms,
 * bathrooms, parking) are MINIMUMS, price and m2 are min/max ranges, origin is multi-choice.
 */

export type InventoryFilterValues = Record<string, unknown>;

/** Applied on first load; they do not make the empty state a "filtered" one. */
export const INVENTORY_DEFAULT_FILTERS: InventoryFilterValues = {
  status: "disponible",
};

/** Filter keys of the numeric controls (minimums and ranges). */
export const INVENTORY_NUMBER_KEYS = [
  "price@gte",
  "price@lte",
  "levels@gte",
  "bedrooms@gte",
  "bathrooms@gte",
  "parking@gte",
  "area_m2@gte",
  "area_m2@lte",
] as const;
export type InventoryNumberKey = (typeof INVENTORY_NUMBER_KEYS)[number];

export const ORIGIN_FILTER_KEY = "origin@in";

/** Only a "Renta" operation can be furnished or unfurnished. */
export const RENTA_OPERATION = "renta";

const isBlank = (value: unknown): boolean => value == null || value === "";

/** Origins selected in the filter (the `origin@in` value is a "(a,b)" list). */
export const readOrigins = (
  filter: InventoryFilterValues,
): InventoryOrigin[] => {
  const raw = filter[ORIGIN_FILTER_KEY];
  if (typeof raw !== "string") return [];
  return raw
    .replace(/^\(|\)$/g, "")
    .split(",")
    .map((part) => part.trim())
    .filter((part): part is InventoryOrigin =>
      (INVENTORY_ORIGINS as readonly string[]).includes(part),
    );
};

/** The text shown in a numeric control ("" when the filter is unset). */
export const readFilterText = (
  filter: InventoryFilterValues,
  key: string,
): string => {
  const value = filter[key];
  return isBlank(value) ? "" : String(value);
};

const withoutKey = (
  filter: InventoryFilterValues,
  key: string,
): InventoryFilterValues =>
  Object.fromEntries(Object.entries(filter).filter(([k]) => k !== key));

/** A non-negative finite number from the control text, or null when it is not one. */
const parseNonNegative = (text: string): number | null => {
  if (text.trim() === "") return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

/**
 * Returns `filter` with `key` set to `value`. A blank value (or a non-numeric / negative text on
 * a numeric key) clears the key; origins are stored as an `in` list; choosing anything but Renta
 * as the operation also clears the furnished filter (it only exists for rentals).
 */
export const setInventoryFilter = (
  filter: InventoryFilterValues,
  key: string,
  value: string | readonly string[] | undefined,
): InventoryFilterValues => {
  if (key === ORIGIN_FILTER_KEY) {
    const origins = (Array.isArray(value) ? value : []).filter(
      (origin: string) => origin !== "",
    );
    return origins.length === 0
      ? withoutKey(filter, key)
      : { ...filter, [key]: `(${origins.join(",")})` };
  }
  const text = typeof value === "string" ? value : "";
  if ((INVENTORY_NUMBER_KEYS as readonly string[]).includes(key)) {
    const parsed = parseNonNegative(text);
    return parsed == null
      ? withoutKey(filter, key)
      : { ...filter, [key]: parsed };
  }
  const next =
    text.trim() === "" ? withoutKey(filter, key) : { ...filter, [key]: text };
  return key === "operation" && text !== RENTA_OPERATION
    ? withoutKey(next, "furnished")
    : next;
};

/** How many filters the user has set beyond the defaults (for the mobile panel's badge). */
export const countActiveInventoryFilters = (
  filter: InventoryFilterValues,
): number =>
  Object.entries(filter).filter(
    ([key, value]) =>
      !isBlank(value) &&
      !(
        key in INVENTORY_DEFAULT_FILTERS &&
        INVENTORY_DEFAULT_FILTERS[key] === value
      ),
  ).length;

type TypeLike = { code: string; label: string; archived_at?: string | null };

/**
 * Property type choices: the active catalogue types in catalogue order plus every archived type
 * still in `usedCodes` (a type used by a listed row), so a row's type is always selectable.
 * An unknown used code falls back to the code as its label.
 */
export const buildPropertyTypeChoices = (
  types: readonly TypeLike[],
  usedCodes: readonly string[] = [],
): { id: string; name: string }[] => {
  const used = new Set(usedCodes);
  const choices = types
    .filter((type) => type.archived_at == null || used.has(type.code))
    .map((type) => ({ id: type.code, name: type.label }));
  const known = new Set(types.map((type) => type.code));
  const unknown = Array.from(used)
    .filter((code) => !known.has(code))
    .map((code) => ({ id: code, name: code }));
  return [...choices, ...unknown];
};
